import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MatchGamesList from '../components/MatchGamesList';
import StreamLinks from '../components/StreamLinks';
import TeamRoster from '../components/TeamRoster';
import { formatDate, format12HourTime } from '../utils/formatters';
import placeholderImage from '../assets/images/placeholder.png';
import Seo from '../components/Seo';
import TeamCrest from '../components/TeamCrest';
import { sportsEventSchema, breadcrumbSchema } from '../utils/structuredData';
import { isCompletedMatch } from '../utils/match';
import '../styles/MatchDetails.css';

const MatchDetails = () => {
  const { id, date } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('games');
  const [activeGame, setActiveGame] = useState(1);

  useEffect(() => {
    const loadMatchDetails = async () => {
      setIsLoading(true);
      try {
        let url = `/api/rl/public/match-status?matchid=${encodeURIComponent(id)}`;
        const safeDate = date && date !== 'null' ? date : null;
        if (safeDate) url += `&date=${encodeURIComponent(safeDate)}`;
        const res = await fetch(url);
        const matchData = await res.json();
        setMatch(matchData[0]);
      } catch (error) {
        console.error("Error loading match details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      loadMatchDetails();
    }
  }, [id, date]);

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="spinner"></div>
    );
  }

  if (!match || !Array.isArray(match.teams)) {
    return (
      <>
        <Seo title="Match not found" noindex />
        <div className="no-matches">Match not found</div>
      </>
    );
  }

  // Find team objects
  const team1 = match.teams.find(team => team.designation === 1);
  const team2 = match.teams.find(team => team.designation === 2);

  // Not `completed` directly: the field is absent on
  // the list feeds, and treating absent as "not completed" renders a finished
  // match as LIVE with no score.
  const completed = isCompletedMatch(match);

  // Check if match is upcoming, live, or completed
  const matchDate = new Date(`${match.date}T${match.time}`);
  const now = new Date();
  let status = '';
  let statusClass = '';
  let team1Results;
  let team2Results;

  if (completed) {
    status = 'Completed';
    statusClass = 'status-completed';
    team1Results = team1.results.reduce((sum, item) => sum + item.result, 0);
    team2Results = team2.results.reduce((sum, item) => sum + item.result, 0);
  } else if (matchDate > now) {
    status = 'Upcoming';
    statusClass = 'status-upcoming';
  } else {
    status = 'Live';
    statusClass = 'status-live';
  }

  // Format date and time
  const formattedDate = formatDate(match.date);
  const formattedTime = format12HourTime(match.time);

  // Get games count for selector
  const gamesCount = completed && match.metadata?.games?.length 
    ? match.metadata.games.length 
    : match.match_format.count;

  // Get player stats for active game
  const renderPlayerStats = (team) => {
    if (!team || !team.players) return null;

    return (
      <div className="player-stats-table">
        <div className="player-stats-header">
          <div className="player-name">Player</div>
          <div className="player-stat">Score</div>
          <div className="player-stat">Goals</div>
          <div className="player-stat">Assists</div>
          <div className="player-stat">Saves</div>
          <div className="player-stat">Shots</div>
        </div>
        {team.players.map(player => {
          const gameStats = player.results?.find(result => result.game_number === activeGame);

          // Check if all stats are 0 or undefined
          const allStatsZero = !gameStats || (
            (!gameStats.score || gameStats.score === 0) &&
            (!gameStats.goals || gameStats.goals === 0) &&
            (!gameStats.assists || gameStats.assists === 0) &&
            (!gameStats.saves || gameStats.saves === 0) &&
            (!gameStats.shots || gameStats.shots === 0)
          );

          return (
            <div key={player.id} className={`player-stats-row ${allStatsZero ? 'did-not-play' : ''}`}>
              <div className="player-name">
                <div className="player-avatar-wrapper">
                  <img
                    src={player.image || placeholderImage}
                    alt={player.username}
                    className="player-avatar"
                    onError={(e) => { e.target.src = placeholderImage }}
                  />
                </div>
                <span className="player-username">{player.username}</span>
              </div>
              {allStatsZero ? (
                <div className="did-not-play-message">Did not play in this match</div>
              ) : (
                <>
                  <div className="player-stat">{gameStats?.score || '0'}</div>
                  <div className="player-stat">{gameStats?.goals || '0'}</div>
                  <div className="player-stat">{gameStats?.assists || '0'}</div>
                  <div className="player-stat">{gameStats?.saves || '0'}</div>
                  <div className="player-stat">{gameStats?.shots || '0'}</div>
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const canonicalPath = match.date ? `/match/${id}/${match.date}` : `/match/${id}`;
  const t1 = match.team_1_name || 'TBD';
  const t2 = match.team_2_name || 'TBD';
  const league = match.league || 'Rocket League';
  const matchTitle = `${t1} vs ${t2} — ${league} Rocket League`;
  const seoDescription =
    completed
      ? `${t1} vs ${t2} final score ${team1Results}–${team2Results} in ${league}. Game-by-game results and player stats on JoltStats.`
      : `${t1} vs ${t2} in ${league} — ${formattedDate} at ${formattedTime}. Live scores and stats on JoltStats.`;
  const seoParent =
    completed
      ? { name: 'Results', path: '/completed-matches' }
      : { name: 'Upcoming Matches', path: '/upcoming-matches' };

  return (
    <div className="match-details">

      <Seo
        title={matchTitle}
        description={seoDescription}
        canonical={canonicalPath}
        type="article"
        schemas={[
          sportsEventSchema({ match, canonicalPath }),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            seoParent,
            { name: `${t1} vs ${t2}`, path: canonicalPath },
          ]),
        ]}
      />

      {/* The hero carries the title visually; this gives the page a real
          heading for assistive tech and search without duplicating it. */}
      <h1 className="sr-only">{t1} vs {t2} — {league}</h1>

      {/* Page header: back + status badge */}
      <div className="page-header match-page-header">
        <button onClick={handleBack} className="back-link">← Back</button>
        <span className={`match-status-badge ${statusClass}`}>{status}</span>
      </div>

      {/* Hero matchup */}
      <div className="match-hero">
        <div className="hero-team">
          <TeamCrest name={t1} size="lg" />
          <span className="hero-team-name">{t1}</span>
        </div>

        <div className="hero-center">
          {completed ? (
            <div className="hero-score">
              <span className={team1Results >= team2Results ? 'score-win' : 'score-loss'}>{team1Results}</span>
              <span className="score-sep">–</span>
              <span className={team2Results >= team1Results ? 'score-win' : 'score-loss'}>{team2Results}</span>
            </div>
          ) : (
            <span className="hero-vs">VS</span>
          )}
          {/* Separators are drawn in CSS so a wrap can't strand a trailing dot */}
          <div className="hero-meta">
            <span>{formattedDate}</span>
            <span>{formattedTime}</span>
            {match.match_format?.count ? <span>BO{match.match_format.count}</span> : null}
          </div>
          {match.league && <p className="hero-league">{match.league}</p>}
        </div>

        <div className="hero-team hero-team--right">
          <TeamCrest name={t2} size="lg" />
          <span className="hero-team-name">{t2}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="match-tabs" role="tablist" aria-label="Match details">
        {[['games', 'Games'], ['stats', 'Stats'], ['streams', 'Streams'], ['roster', 'Rosters']].map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={activeTab === key}
            className={`match-tab ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Games */}
      {activeTab === 'games' && (
        <div className="tab-panel">
          <MatchGamesList match={match} />
        </div>
      )}

      {/* Player Stats */}
      {activeTab === 'stats' && (
        <div className="tab-panel">
          {!completed ? (
            <p className="tab-empty">Stats will be available once the match is completed.</p>
          ) : (
            <div className="player-stats-container">
              <div className="game-selector">
                {Array.from({ length: gamesCount }, (_, i) => i + 1).map(gameNum => (
                  <button
                    key={gameNum}
                    className={`filter-btn ${activeGame === gameNum ? 'active' : ''}`}
                    onClick={() => setActiveGame(gameNum)}
                    disabled={!team1?.players?.some(p => p.results?.some(r => r.game_number === gameNum))}
                  >
                    Game {gameNum}
                  </button>
                ))}
              </div>
              <div className="teams-stats">
                <div className="team-stats">
                  <h3 className="team-stats-heading">{match.team_1_name}</h3>
                  {renderPlayerStats(team1)}
                </div>
                <div className="team-stats">
                  <h3 className="team-stats-heading">{match.team_2_name}</h3>
                  {renderPlayerStats(team2)}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Streams */}
      {activeTab === 'streams' && (
        <div className="tab-panel">
          <StreamLinks links={match.stream_links} />
        </div>
      )}

      {/* Rosters */}
      {activeTab === 'roster' && (
        <div className="tab-panel">
          <TeamRoster teams={match.teams} />
        </div>
      )}

    </div>
  );
};

export default MatchDetails;
